<template>
  <v-expansion-panel :id="`${testId}`" v-show="true">
    <v-expansion-panel-header>
      <h2 id="sensorineural-test-title">{{ testName }}</h2>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        class="grid-container"
        id="sensorineural-test-grid"
      >
        <v-container>
          <v-row>
            <v-col class="custom-col">
              <p>
                For comprehensive diagnostic testing for non-syndromic bilateral
                SNHL, we recommend requesting the
                <a
                  target="_blank"
                  href="https://www.vcgs.org.au/tests/deafness-mlpa/"
                >
                  MLPA test in conjunction with our exome sequencing test</a
                >. This will detect sequence variants in deafness associated
                genes in the PanelApp Australia
                <a href="https://panelapp-aus.org/panels/209/" target="_blank"
                  >deafness gene panel</a
                >.
              </p>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Clinical features</span>
              <template v-if="brokenPhenotypesApi">
                <BrokenApiAlert type="phenotypes" />
              </template>
              <template v-else>
                <v-autocomplete
                  v-model="phenotypes"
                  :id="testId + '.phenotypes'"
                  :items="phenotypeList"
                  :menu-props="{ maxHeight: '200' }"
                  multiple
                  chips
                  persistent-hint
                  deletable-chips
                  @input="searchInputPhenotypes = null"
                  :search-input.sync="searchInputPhenotypes"
                  outlined
                  hide-details="auto"
                  @update:search-input="handlePhSearchChange"
                  :filter="filterPhItems"
                >
                  <template v-slot:prepend-item v-if="isSearchingHp">
                    <v-progress-linear indeterminate></v-progress-linear>
                  </template>
                  <template v-slot:no-data>
                    <div class="px-4">{{ phSearchMsg }}</div>
                  </template>
                </v-autocomplete>
                <span class="text-hint"
                  >Please start typing the main clinical features. e.g.
                  intellectual disability. Select the most appropriate HPO term
                  from those suggested. Detailed clinical information helps us
                  diagnose patients more accurately. The phenotype's are sourced
                  from hpo.jax.org.</span
                >
              </template>
              <template v-if="badSearchQuery">
                <BadSearchQueryAlert/>
              </template>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Family history</span>
              <v-textarea
                rows="1"
                auto-grow
                v-model="familyHx"
                :id="testId + '.familyHx'"
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. affected status of the parents, parental consanguinity,
                etc.</span
              >
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Clinical notes</span>
              <v-textarea
                rows="2"
                auto-grow
                v-model="clinicalNotes"
                :id="testId + '.clinicalNotes'"
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. results of relevant prior genetic and non-genetic
                investigations, biochemistry, histopathology and imaging,
                etc.</span
              >
            </v-col>
          </v-row>
        </v-container>

        <SensorineuralConsentContainer
          ref="sensorineuralConsent"
          :is-trio="false"
          :force-form-dirty="forceFormDirty"
          :testName="testName"
          :testId="testId"
        ></SensorineuralConsentContainer>

        <TestPriceContainer :price="price" />
      </v-form>
      <v-btn
        class="primary-btn btn-padding-medium top-spaced-item"
        @click="goToNextSection()"
      >
        Continue to next section
      </v-btn>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import PhenotypesMixin from "../../mixins/PhenotypesMixin";
import FormRulesMixin from "../../mixins/FormRulesMixin";
import SensorineuralConsentContainer from "./SensorineuralConsentContainer";
import TestPriceContainer from "../common/TestPriceContainer";
import { EventBus } from "@/event-bus";
import { useSectionStore } from "../../store/SectionStore";
import RetrieveJSONMixin from "../../mixins/RetrieveJSONMixin.vue";
import BrokenApiAlert from "../common/BrokenApiAlert.vue";
import BadSearchQueryAlert from "../common/BadSearchQueryAlert.vue";

export default {
  props: {
    testName: String,
    testId: String,
    patientdata: Object,
    forceFormDirty: {
      default: false,
      type: Boolean,
    },
  },
  async mounted() {
    EventBus.$emit("handleTestsLoadingStatus", true, this.testId);
    this.sensorineuralTestPricing = await this.execRetrieveJson(
      "json/pricing/sensorineural_pricing.json"
    );
    EventBus.$emit("handleTestsLoadingStatus", false, this.testId);

    EventBus.$emit("getPatientData");

    const element = document.getElementById("sensorineural-test-title");

    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }

    this.urgency = "Standard";
    this.familyType = "Singleton";
    this.analysisType = "Exome Focused";

    const pricing = this.sensorineuralTestPricing[this.urgency][
      this.familyType
    ][this.analysisType];

    this.price = pricing.price;
    this.testCode = pricing.testCode;
    this.medicare = pricing.medicare;

    this.$emit("toggleTotalPrice", this.price, this.testId);
  },
  mixins: [FormRulesMixin, PhenotypesMixin, RetrieveJSONMixin],
  name: "",
  components: {
    SensorineuralConsentContainer,
    TestPriceContainer,
    BrokenApiAlert,
    BadSearchQueryAlert,
  },
  data: () => ({
    valid: false,
    urgent: false,
    urgentReason: "",
    sensorineuralTestPricing: null,
    cgApproved: true,
    cgDetails: "",
    genePanels: ["Deafness_IsolatedAndComplex"],
    phenotypes: [],
    familyHx: "",
    clinicalNotes: "",
    ageOfOnset: "",
    urgency: null,
    familyType: null,
    analysisType: null,
    searchInputGenes: null,
    searchInputPhenotypes: null,
    price: null,
    testCode: null,
    medicare: false,
  }),
  methods: {
    isFormValid: function() {
      if (typeof this.$refs.sensorineuralConsent !== "undefined") {
        return this.$refs.sensorineuralConsent.isFormValid();
      } else return false;
    },

    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      const currIndex = state.testOrderDom.indexOf(`${this.testId}`);
      if (currIndex + 1 < state.AddedOrderofPanels.length) {
        setTimeout(() => {
          this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
        }, 500);
        state.nextPanel(state.testOrderDom[currIndex + 1]);
      } else {
        //If there is no section after the currently opened section
        state.panel = [];
      }
    },
    transferData: function() {
      let familyDetails = [];

      for (let [familyKey, familyComponent] of Object.entries(this.$refs)) {
        if (familyComponent && familyKey.substring(0, 12) === "familyMember") {
          familyDetails.push(familyComponent.transferData());
        }
      }

      const finalGenePanels =
        this.genePanels[0] === "Deafness_IsolatedAndComplex"
          ? "209 - Deafness_IsolatedAndComplex"
          : "";

      let result = {
        urgent: this.urgent,
        urgency: this.urgency,
        urgentReason: "",
        testType: this.testId,
        familyType: this.familyType,
        analysisType: this.analysisType,
        rapid: this.rapidTest,
        genePanels: finalGenePanels,
        phenotypes: this.getFinalPhenotypes(this.phenotypes),
        ageOfOnset: this.ageOfOnset,
        familyHx: this.familyHx,
        clinicalNotes: this.clinicalNotes,
        family: familyDetails,
        quotedPrice: this.dollarPrice(this.price),
        testCode: this.testCode,
        cgApproved: this.cgApproved,
        cgDetails: this.cgDetails,
        medicare: this.medicare,
      };

      // Consent data is present, except if the test is medicare but not approved by gc
      if (this.$refs.sensorineuralConsent) {
        const consentData = this.$refs.sensorineuralConsent.transferData();

        // assign the data fields from consent direct into the top level of our data
        Object.assign(result, consentData);
      }

      return result;
    },
  },
};
</script>
